/* eslint-disable import/no-cycle */
import ReactDOM from 'react-dom';
import React from 'react';
import { Provider } from 'react-redux';
import { isScreenWidthMobile } from '../app/components/custom-hooks/screen-detector';
import VolumeDiscountGroupPreview from '../app/components/Embedded/VolumeDiscountGroupPreview';
import { postData } from '../common/api';
import { createEmbeddedPageRequest } from '../common/apiRequest';
import { getOfferEndpoint, productAddonLayouts } from '../common/constants';
import { createEmebeddedContainerWrapper } from '../common/html';
import { logger, logtail } from '../common/logger';
import {
  getPage,
  getShop,
  isCartPage,
  isShopifyThemeEditor,
} from '../common/pageData';
import {
  setPdpFgVariantsWithQuantity,
  setPdpFgWidgetList,
} from '../common/settings';
import { sleep } from '../common/time';
import {
  onManualFreeGiftEligibleResponse,
  requestForPdpFgUsingStoredData,
} from '../freegift';
import {
  addOfferedVariantsToCart,
  changeCartVartiantWithoutProperties,
  getBundleOrAddonOrFBTVariantsToAdd,
} from '../common/cart';

import { GlobalStyle } from '../app/components/skins';
// eslint-disable-next-line import/no-cycle
import Init from '../app/containers/Init';
import EmbeddedPage from '../app/containers/EmbeddedPage';
import { setOfferViewed } from '../common/offer';
import {
  getMerchantSpecificThemeFlags,
  getIsProductAddonsSelected,
} from '../app/components/helpers/offer';
import VolumeDiscountPage from '../app/containers/VolumeDiscountPage';
import { goToCart } from '../common/navigation';

import { addToCartSuccess } from '../app/containers/Init/actions';
import { StaticMutexKeys, mutexManager } from '../common/mutexmanager';
import { reloadPage } from '../pageHelper';
import {
  isOfferValidForCurrentPage,
  filterWidgets,
  isFreegiftWithCodeEligible,
  onFreegiftNotEligibleResponse,
  onModuleOfferEligible,
  setFreeGiftDiscountCodesMapping,
} from './commonModuleForEmbeddedAndAutoAdd';
import {
  getOfferDataForLogs,
  logRuntimeError,
  logtailMiddleware,
} from '../common/utils';

export async function checkEmbeddedWidgetsByTriggerPage({
  widgets,
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  productFormSelector,
}) {
  const filtered = filterWidgets({
    offerTypes: ['crossell', 'freegift', 'vd', 'fbt'],
    displayType: 'embedded',
    availingType: 'manual_selection',
    widgets,
  });

  for (let i = 0; i < filtered.length; i += 1) {
    if (isOfferValidForCurrentPage({ widget: filtered[i] })) {
      checkEmbeddedWidgetOffersAndDisplay({
        widget: filtered[i],
        cartPageDiscountConfig,
        isDiscountCodeEnabled,
        productFormSelector,
      });
    }
  }

  // if there is pdp fg data present in storage, it meeans that pdp fg offer had become eligible in the same session.
  // in that case, we have to send an offer request from the cart. this is done because if the primary product has been removed, we'll need to auto remove the fg

  if (getPage() === 'cart') {
    requestForPdpFgUsingStoredData({
      cartPageDiscountConfig,
      isDiscountCodeEnabled,
      productFormSelector,
    });
  }

  return filtered;
}

export async function checkEmbeddedCartAndCheckoutPageWidgets({
  widgets,
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  apiCart,
}) {
  for (let i = 0; i < widgets.length; i += 1) {
    const { offerType, displayType, triggerType } = widgets[i];
    if (
      displayType === 'embedded' &&
      ['cart_page', 'checkout_page'].includes(triggerType)
    ) {
      if (['crossell', 'freegift'].includes(offerType)) {
        checkEmbeddedWidgetOffersAndDisplay({
          widget: widgets[i],
          cartPageDiscountConfig,
          isDiscountCodeEnabled,
          apiCart,
        });
      }
    }
  }
}

export async function checkEmbeddedCartPageFreegiftWidgets({
  widgets,
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  apiCart,
}) {
  for (let i = 0; i < widgets.length; i += 1) {
    const { offerType, displayType, triggerType } = widgets[i];
    if (displayType === 'embedded' && triggerType === 'cart_page') {
      if (['freegift'].includes(offerType)) {
        checkEmbeddedWidgetOffersAndDisplay({
          widget: widgets[i],
          cartPageDiscountConfig,
          isDiscountCodeEnabled,
          apiCart,
        });
      }
    }
  }
}

export async function checkEmbeddedProductPageWidgets({
  widgets,
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  apiCart,
  productFormSelector,
}) {
  for (let i = 0; i < widgets.length; i += 1) {
    const { offerType, displayType, triggerType } = widgets[i];
    if (displayType === 'embedded') {
      if (
        ['crossell', 'freegift', 'vd', 'fbt'].includes(offerType) &&
        triggerType === 'product_page'
      ) {
        checkEmbeddedWidgetOffersAndDisplay({
          widget: widgets[i],
          cartPageDiscountConfig,
          isDiscountCodeEnabled,
          apiCart,
          productFormSelector,
        });
      }
    }
  }
}

export async function checkEmbeddedWidgetOffersAndDisplay({
  widget,
  apiCart,
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  productFormSelector,
  isCalledFromCartDrawer = false,
  customVariantsWithQuantity = [],
}) {
  const { id, availingType, offerType, triggerType, mrWidgetID } = widget;

  let { sections } = widget;

  const widgetMutex = mutexManager.getMutex(id.toString());

  const fgMutex = mutexManager.getMutex(StaticMutexKeys.FREEGIFT);
  if (offerType === 'freegift') {
    await fgMutex.acquire();
  }

  try {
    // ensure that a widget is being evaluated only once at a time
    if (!widgetMutex.isFree()) {
      logger.log('duplicate');
      return;
    }

    await widgetMutex.acquire();

    // some stores need a delay before the offer is checked
    await embeddedWidgetDelayIfNeeded();

    // create the request
    const req = await createEmbeddedPageRequest({
      source: triggerType,
      offerType,
      apiCart,
      widget,
      productFormSelector,
      isCalledFromCartDrawer,
      customVariantsWithQuantity,
    });

    // send the request
    const response = await postData(getOfferEndpoint(), req);
    if (!response) {
      return;
    }
    let { is_eligible } = response;
    const { offer } = response;

    logtailMiddleware({
      widgetId: widget.id,
      basicOfferData: getOfferDataForLogs({ offerType, offer }),
      offerName: offerType,
    });

    if (offerType === 'freegift') {
      await setFreeGiftDiscountCodesMapping(response, widget);
    }

    // update eligibility status if offer is freegift with discount code
    const {
      isFreegift,
      is_eligible: isFgWithCodeEligible,
    } = isFreegiftWithCodeEligible({
      offerType,
      is_eligible,
      offer,
      cartPageDiscountConfig,
      isDiscountCodeEnabled,
    });
    if (isFreegift) {
      is_eligible = isFgWithCodeEligible;
    }

    // functions to be called when offer is eligible
    if (is_eligible) {
      await onModuleOfferEligible({ offer });
      sections = filterSections({
        sections,
        offerType,
        offer,
        triggerType,
      });

      await onManualFreeGiftEligibleResponse({
        offer,
        mrCart: req.cart,
        widget,
        isCalledFromCartDrawer,
        skipExecuteFgConditions: false,
      });

      // for free git auto add there is no widget to display
      if (offerType === 'freegift' && availingType === 'auto_add') {
        return;
      }
    }

    const isPdpFgCalledFromDrawer =
      offerType === 'freegift' &&
      triggerType === 'product_page' &&
      isCalledFromCartDrawer;

    if (!isPdpFgCalledFromDrawer) {
      // display / hide the widget
      displayEmbeddedWidget({
        offer,
        sections,
        cartPageDiscountConfig,
        isDiscountCodeEnabled,
        req,
        triggerType,
        offerType,
        availingType,
        mrWidgetID,
        is_eligible,
        widget,
        isCalledFromCartDrawer,
        widgetID: id,
      });
    }

    const isPdpFgOfferOnProductPage =
      getPage() === 'product' &&
      offerType === 'freegift' &&
      triggerType === 'product_page' &&
      !isCalledFromCartDrawer;

    // functions to run if offer is not eligible
    // we can't auto remove if pdp fg isn't eligible in a product page. Auto remove can only happen in cart.
    // for ex, if offer is set to be eligible on a particular collection, it won't be eligible when customer visits a pdp of a product that is outside of the collection.
    // in that case, we can't run onFreegiftNotEligibleResponse function because it will lead to auto removing the freegift which is, wrong.
    if (!is_eligible && !isPdpFgOfferOnProductPage) {
      await onFreegiftNotEligibleResponse({
        offerType,
        availingType,
        mrCart: req.cart,
        widget,
      });
    }
  } catch (e) {
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: {
        fileName: 'embeddedModule',
        level: 'error',
        functionName: 'checkEmbeddedWidgetOffersAndDisplay',
      },
      errorStack: e,
    });
  } finally {
    widgetMutex.release();
    fgMutex.release();
  }
}

const embeddedWidgetDelayIfNeeded = async () => {
  if (getShop() === 'party-love-australia.myshopify.com') {
    await sleep(1800);
  }

  if (getShop() === 'knya-med.myshopify.com') {
    await sleep(1800);
  }

  if (getShop() === 'devoteewear.myshopify.com') {
    await sleep(1800);
  }
};

export const filterSections = ({ sections, offerType, offer }) => {
  const {
    theme: { mobileLayoutType, desktopLayoutType, widgetPosition },
  } = offer;

  const isMobile = isScreenWidthMobile();

  const isOfferInReferenceToATC =
    offerType === 'vd' ||
    getIsProductAddonsSelected({
      offerType,
      mobileLayoutType,
      desktopLayoutType,
      isMobile,
      productAddonLayouts,
    });

  if (isOfferInReferenceToATC) {
    return sections.filter(({ sectionType }) => sectionType === widgetPosition);
  }

  if (sections) {
    // for all the other cases remove the above atc section
    return sections.filter(({ sectionType }) => sectionType !== 'above_atc');
  }
  return [];
};

export const displayEmbeddedWidget = async ({
  offer,
  sections,
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  req,
  triggerType,
  offerType,
  availingType,
  mrWidgetID,
  is_eligible,
  widget,
  isCalledFromCartDrawer,
  widgetID,
}) => {
  // some stores need a delay before rendering
  await displayDelayIfNeeded();

  const containers = [];
  for (let i = 0; i < sections.length; i += 1) {
    const section = sections[i];
    const { selector, position, sectionType } = section;

    // as the same selectors are set for page and drawer for old customers so added this check
    // else 2 widgets are being displayed
    if (sectionType === 'cart_page' && !isCartPage()) {
      continue;
    }

    containers.push({
      selector,
      position,
      containerID: getEmbeddedContainerID({
        triggerType,
        offerType,
        availingType,
        sectionType,
        mrWidgetID,
      }),
    });
  }

  logger.log('containers', containers, is_eligible);
  const isPdpFg = triggerType === 'product_page' && offerType === 'freegift';
  // if not eligible then clos the offers in all containers
  if (!is_eligible) {
    for (let i = 0; i < containers.length; i += 1) {
      const { containerID } = containers[i];
      renderEmbddedInDiv({
        closeOffer: true,
        containerID,
        offer: {},
        req,
        widgetID,
      });
    }
    return;
  }

  const { mr_offer_id } = offer;

  const tasks = [];
  for (let i = 0; i < containers.length; i += 1) {
    const { containerID, selector, position } = containers[i];
    tasks.push(
      createContainerAndRender({
        req,
        offer,
        variantsWithQuantity: req.variantsWithQuantity,
        containerID,
        selector,
        position,
        retryContainerCreation: true,
        widgetID,
      }),
    );
  }

  const isPdpFgRenderedOnProductPage = isPdpFg && getPage() === 'product';
  const result = await Promise.allSettled(tasks);
  const rendered = result.reduce((acc, curr) => acc || curr.value, false);
  if (rendered) {
    await setOfferViewed({
      mr_offer_id,
      checkDiscount: false,
      cartPageDiscountConfig,
      isDiscountCodeEnabled,
    });

    // Incase of poduct page fg, we need to store the data in session storage since we have to send the offer request in cart
    // while sending the request from cart, we fetch the data from sessionStorage and send it throgh payload.
    if (isPdpFgRenderedOnProductPage && !isCalledFromCartDrawer) {
      setPdpFgWidgetList(widget);
      setPdpFgVariantsWithQuantity({
        widgetID: widget.id,
        variantsWithQuantity: offer.variantsWithQuantity,
        offerID: mr_offer_id,
      });
    }
  }
};

const getEmbeddedContainerID = ({
  triggerType,
  offerType,
  availingType,
  sectionType,
  mrWidgetID,
}) => {
  // as the ids were predefined and old stores have this id, we cannot change it
  let trigger = 'cp';
  let offer = offerType;
  let section = sectionType;

  if (triggerType === 'product_page') {
    trigger = 'prp';
  }

  if (offerType === 'crossell') {
    offer = 'any';
  }

  if (offerType === 'freegift' && availingType === 'manual_selection') {
    offer = 'freegift_manual';
  }

  switch (sectionType) {
    case 'above_atc':
    case 'below_atc':
    case 'cart_page':
      section = 'page';
      break;

    case 'cart_drawer':
      section = 'drawer';
      break;

    default:
      break;
  }

  let id = `mr-div-embedded-${trigger}-${offer}-${section}`;
  if (mrWidgetID) {
    id = `${id}-${mrWidgetID}`;
  }
  return id;
};

const displayDelayIfNeeded = async () => {
  if (getShop() === 'vilvahstore.myshopify.com') {
    await sleep(1000);
  }
};

const createContainerAndRender = async ({
  req,
  offer,
  variantsWithQuantity,
  containerID,
  selector,
  position,
  retryContainerCreation,
  widgetID,
}) => {
  const existing = document.getElementById(containerID);
  if (existing) {
    renderEmbddedInDiv({
      req,
      offer,
      variantsWithQuantity,
      containerID,
      closeOffer: false,
      widgetID,
    });
    return true;
  }

  const selectors = selector.split(',');
  const positions = position.split(',');
  let container;
  for (let i = 0; i < selectors.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    container = await createEmebeddedContainerWrapper({
      id: containerID,
      selector: selectors[i].trim(),
      position: positions[i],
      retryContainerCreation,
    });

    if (container) {
      break;
    }
  }

  if (container) {
    renderEmbddedInDiv({
      req,
      offer,
      variantsWithQuantity,
      containerID,
      closeOffer: false,
      widgetID,
    });
    return true;
  }

  return false;
};

function renderEmbddedInDiv({
  req,
  offer,
  variantsWithQuantity,
  containerID,
  closeOffer,
  widgetID,
}) {
  const {
    mr_offer_id: mrOfferID,
    offered: products,
    primary,
    theme = {},
    isDiscountDisabled,
    isTimerDisabled,
    triggerType,
    type: offerType,
    currencyFormat,
    currencySymbol,
    decimalSymbol,
    thousandSeparator,
    discountType,
    discountValue,
    discountQuantity,
    isDiscountQuantityRestricted,
    isHideAfterProductAddition,
    isNumberOfOfferedProductsRestricted,
    offerLevelDiscountQuantity,
    isBrandingEnabled,
    fbtDiscountValue,
    fbtPartialDiscountValue,
    isFBTDiscountDisabled,
    isFBTPartialDiscountDisabled,
    isApplyDiscountOnCompareAtPrice,
    priceRoundType,
    discountRoundType,
    vdOptions,
    isVDMultipleVariantsSelectable,
    vdAvailingMethod,
    vdGroupOfItemsPageURL,
    isShowVDViewProductsButton,
    isVDQuantityOptionSelected,
    vdSelectedQuantityOptionIndex,
    reservedItemQuantity,
    isRepeatRules,
    rulesEligibilityCount,
  } = offer;

  const offerLevelDiscountQuantityToUse =
    offerType === 'freegift'
      ? offerLevelDiscountQuantity * rulesEligibilityCount
      : offerLevelDiscountQuantity;

  const discountQuantityToUse =
    offerType === 'freegift'
      ? discountQuantity * rulesEligibilityCount
      : discountQuantity;

  const { cart } = req;

  logger.log('render embedded', !closeOffer);
  const isMobile = isScreenWidthMobile();

  if (closeOffer && isShopifyThemeEditor()) {
    // for shopify theme editor leave the div as it is
    // TODO: handle case where the offer is displayed and then removed
    // so that dummy values can be displayed
    return;
  }

  if (closeOffer) {
    hideElementsOnOfferClose({ reqOfferType: req.offerType });
  }

  const start = performance.now();
  const el = document.getElementById(containerID);
  if (el) {
    if (containerID === 'mr-div-embedded-cp-freegift_manual-page') {
      if (
        [
          'knack-roularta.myshopify.com',
          'libelle-lekker-roularta.myshopify.com',
          'test-sub-rmg.myshopify.com',
        ].includes(getShop())
      ) {
        el.style.width = '100%';
      }
    }

    if (['swiftoutdoors.myshopify.com'].includes(getShop())) {
      el.style.width = '100%';
    }

    // TODO: find a way to remove this and generalise container width
    if (
      containerID === 'mr-div-embedded-prp-any-page' &&
      [
        'quirky-perks-new.myshopify.com',
        'little-sky-stone.myshopify.com',
      ].includes(getShop())
    ) {
      el.style.width =
        'var(--product-info-width, calc(100vw - calc(2 * var(--container-gutter))))';
    }

    if (
      containerID === 'mr-div-embedded-prp-any-page' &&
      getShop() === 'sudofoods.myshopify.com'
    ) {
      el.style.width = '350px';
    }

    if (getShop() === 'yoga-studio-store.myshopify.com') {
      el.style.marginBottom = '12px';
    }

    if (
      containerID === 'mr-div-embedded-cp-freegift_manual-drawer' &&
      getShop() === 'elizabeth-scarlett.myshopify.com'
    ) {
      el.style.marginTop = '-35px';
      el.style.marginBottom = '10px';
    }

    if (getShop() === 'moonglow-jewelry.myshopify.com') {
      el.style.margin = '5px 0 0 0';
    }

    if (closeOffer) {
      // reset
      el.style.marginBottom = '0';
      el.style.marginTop = '0';
    }

    const themeWithMerchantSpecificFlags = getMerchantSpecificThemeFlags({
      shopURL: getShop(),
      theme,
      offerType: `embedded-${offerType}`,
    });

    if (offerType === 'vd') {
      ReactDOM.render(
        <Provider store={store}>
          <div>
            <GlobalStyle {...themeWithMerchantSpecificFlags} />
            <Init />
            {/* working */}
            {!closeOffer &&
              vdAvailingMethod === 'specific_item' && (
                <VolumeDiscountPage
                  mrOfferID={mrOfferID}
                  primary={primary}
                  vdOptions={vdOptions}
                  theme={themeWithMerchantSpecificFlags}
                  isVDMultipleVariantsSelectable={
                    isVDMultipleVariantsSelectable
                  }
                  discountType={discountType}
                  currencyFormat={currencyFormat}
                  currencySymbol={currencySymbol}
                  priceRoundType={priceRoundType}
                  discountRoundType={discountRoundType}
                  isVDQuantityOptionSelected={isVDQuantityOptionSelected}
                  vdSelectedQuantityOptionIndex={vdSelectedQuantityOptionIndex}
                  widgetID={widgetID}
                />
              )}
            {!closeOffer &&
              vdAvailingMethod === 'group_of_items' && (
                <VolumeDiscountGroupPreview
                  isShowVDViewProductsButton={isShowVDViewProductsButton}
                  vdGroupOfItemsPageURL={vdGroupOfItemsPageURL}
                  theme={themeWithMerchantSpecificFlags}
                  isMobile={isMobile}
                  vdOptions={vdOptions}
                  discountType={discountType}
                  currencyFormat={currencyFormat}
                  currencySymbol={currencySymbol}
                  decimalSymbol={decimalSymbol}
                  thousandSeparator={thousandSeparator}
                />
              )}
          </div>
        </Provider>,
        el,
      );
    } else {
      ReactDOM.render(
        <Provider store={store}>
          <div>
            <GlobalStyle {...themeWithMerchantSpecificFlags} />
            <Init />
            {!closeOffer && (
              <EmbeddedPage
                reservedItemQuantity={reservedItemQuantity}
                isRepeatRules={isRepeatRules}
                mrOfferID={mrOfferID}
                products={products}
                primary={primary}
                theme={themeWithMerchantSpecificFlags}
                cart={cart}
                isTimerDisabled={isTimerDisabled}
                isDiscountDisabled={isDiscountDisabled}
                triggerType={triggerType}
                offerType={offerType}
                currencyFormat={currencyFormat}
                currencySymbol={currencySymbol}
                decimalSymbol={decimalSymbol}
                thousandSeparator={thousandSeparator}
                discountType={discountType}
                discountValue={discountValue}
                discountQuantity={discountQuantityToUse}
                isDiscountQuantityRestricted={isDiscountQuantityRestricted}
                isNumberOfOfferedProductsRestricted={
                  isNumberOfOfferedProductsRestricted
                }
                offerLevelDiscountQuantityFromOffer={offerLevelDiscountQuantity}
                offerLevelDiscountQuantityToUse={
                  offerLevelDiscountQuantityToUse
                }
                isHideAfterProductAddition={isHideAfterProductAddition}
                changeCartVartiantWithoutProperties={
                  changeCartVartiantWithoutProperties
                }
                isConsiderRepetetionOfRules={isRepeatRules}
                variantsWithQuantity={variantsWithQuantity}
                isBrandingEnabled={isBrandingEnabled}
                addOfferedVariantsToCart={addOfferedVariantsToCart}
                fbtDiscountValue={fbtDiscountValue}
                fbtPartialDiscountValue={fbtPartialDiscountValue}
                isFBTDiscountDisabled={isFBTDiscountDisabled}
                isFBTPartialDiscountDisabled={isFBTPartialDiscountDisabled}
                isApplyDiscountOnCompareAtPrice={
                  isApplyDiscountOnCompareAtPrice
                }
                priceRoundType={priceRoundType}
                discountRoundType={discountRoundType}
                addProductAddonsToCart={addProductAddonsToCart}
                widgetID={widgetID}
              />
            )}
          </div>
        </Provider>,
        el,
      );
    }

    logger.Debug(performance.now() - start, 'render time');
  }
}

function hideElementsOnOfferClose({ reqOfferType }) {
  let selector = '';
  if (getShop() === 'blaqluxury.myshopify.com' && reqOfferType === 'fbt') {
    selector = '#monk-people-also-bought';
  }

  if (selector) {
    const node = document.querySelector(selector);
    if (node) {
      node.style.display = 'none';
    }
  }
}

export const getProductFormSelectorUsingShopUrl = ({ shopUrl }) => {
  switch (shopUrl) {
    case 'arearugs-com.myshopify.com':
    case 'onepricerugs.myshopify.com':
    case 'fittify-in.myshopify.com':
      return '.product-form form[action$="/cart/add"]';
    case 'swiftoutdoors.myshopify.com':
      return "main form[action$='/cart/add'].shopify-product-form";
    case 'waiflowcustom.myshopify.com':
      return "form[action$='/cart/add'].product-single__form";
    default:
      return "form[action$='/cart/add']";
  }
};

function registerProductFormChange({
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  widgets,
  productFormSelector,
}) {
  const selector =
    productFormSelector ||
    getProductFormSelectorUsingShopUrl({ shopUrl: getShop() });

  const productForm = document.querySelector(selector);
  if (!productForm || getPage() !== 'product') {
    return;
  }
  productForm.addEventListener('change', e => {
    if (e.target.getAttribute('data-mr-input') === 'true') return;

    checkEmbeddedProductPageWidgets({
      widgets,
      cartPageDiscountConfig,
      isDiscountCodeEnabled,
      productFormSelector: selector,
    });
  });

  document.documentElement.addEventListener('mr:productChange', () => {
    checkEmbeddedProductPageWidgets({
      widgets,
      cartPageDiscountConfig,
      isDiscountCodeEnabled,
      productFormSelector: selector,
    });
  });
}

let store = null;
export const setStore = st => {
  store = st;
};

export const init = async ({ config, isCVBEnabled, productFormSelector }) => {
  const { cartPageDiscountConfig, isDiscountCodeEnabled, widgets } = config;

  checkEmbeddedWidgetsByTriggerPage({
    widgets,
    cartPageDiscountConfig,
    isDiscountCodeEnabled,
    productFormSelector,
  });

  registerProductFormChange({
    widgets,
    cartPageDiscountConfig,
    isCVBEnabled,
    isDiscountCodeEnabled,
    productFormSelector,
  });

  registerPostApplyOrRemoveFDCEvent({
    isDiscountCodeEnabled,
    cartPageDiscountConfig,
    widgets,
  });

  registerPostApplyOrRemoveFDCEvent({
    isDiscountCodeEnabled,
    cartPageDiscountConfig,
    widgets,
  });
};

function onPostApplyOrRemoveFDC({
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  widgets,
}) {
  checkEmbeddedCartPageFreegiftWidgets({
    widgets,
    cartPageDiscountConfig,
    isDiscountCodeEnabled,
  });
}

export function registerPostApplyOrRemoveFDCEvent({
  cartPageDiscountConfig,
  isDiscountCodeEnabled,
  widgets,
}) {
  document.addEventListener('mrDiscountCode:apply', () => {
    logger.log('mrDiscountCode:apply custom event called');
    onPostApplyOrRemoveFDC({
      cartPageDiscountConfig,
      isDiscountCodeEnabled,
      widgets,
    });
  });

  document.addEventListener('mrDiscountCode:remove', () => {
    logger.log('mrDiscountCode:remove custom event called');
    onPostApplyOrRemoveFDC({
      cartPageDiscountConfig,
      isDiscountCodeEnabled,
      widgets,
    });
  });
}

export async function addProductAddonsToCart({
  mrOfferID,
  primary,
  selectedCheckboxProducts,
  theme,
  dispatch,
  isAboveATC,
  isSellingPlanEnabled,
  widgetID,
}) {
  const selectedVariantInfo = selectedCheckboxProducts.map(
    product => product.variants[0],
  );

  let variantsToAdd = await getBundleOrAddonOrFBTVariantsToAdd({
    products: selectedCheckboxProducts,
    selectedVariantInfo,
    isDiscountDisabled:
      selectedVariantInfo.reduce(
        (total, variant) => total + variant.calculatedDiscountFloat,
        0,
      ) <= 0,
    discountQuantity: 1,
    isDiscountQuantityRestricted: true,
    theme,
    offerType: 'crossell',
    isSkipIfAlreadyInCart: !isAboveATC,
    isSellingPlanEnabled,
  });

  if (primary !== undefined) {
    variantsToAdd = variantsToAdd.concat(
      Array(primary.quantity).fill({
        product_id: primary.sp_product_id,
        variant_id: primary.sp_variant_id,
        properties: {},
      }),
    );
  }

  logger.log('mr - variantsToAdd', variantsToAdd);
  if (variantsToAdd.length > 0) {
    await addOfferedVariantsToCart(variantsToAdd, false, isSellingPlanEnabled);
    for (let i = 0; i < variantsToAdd.length; i += 1) {
      const { product_id, variant_id } = variantsToAdd[i];
      dispatch(
        addToCartSuccess({
          mrOfferID,
          product_id,
          variant_id,
          isEmbedded: true,
          isProductPage: true,
          theme,
          offerType: 'crossell',
          widgetID,
        }),
      );
    }
  }

  const { isProductButtonActionGoToCart } = theme;
  logger.log('isProductButtonActionGoToCart', isProductButtonActionGoToCart);
  if (isProductButtonActionGoToCart) {
    goToCart({
      isPopup: false,
      isRefreshPageAfterProductAddition:
        theme.isRefreshPageAfterProductAddition,
    });

    if (theme.isRefreshPageAfterProductAddition) {
      return;
    }
  }

  if (theme.isRefreshPageAfterProductAddition) {
    logger.log('refresh initiated 1344');
    reloadPage();
  }
}
